import React from "react";
import PropTypes from "prop-types";
import { Icon } from "@qgiv/core-react";
import { TextButton, Divider } from "@qgiv/core-donor";
import ConnectedParticipantSummaryCategoryPrice from "./ConnectedParticipantSummaryCategoryPrice";
import ParticipantSummaryChangeButton from "./ParticipantSummaryChangeButton";
import ParticipantSummaryRemoveButton from "./ParticipantSummaryRemoveButton";

const ParticipantSummary = ({
    addAdditionalParticipantDisabled,
    baseUrl,
    categoryId = undefined,
    categoryName = undefined,
    changeParticipantDetailsLinkLabel,
    currentRegistration,
    displayDetails,
    displayDivider,
    hasCommitment = undefined,
    isCms,
    isConfirmed,
    isManagedByPrimary,
    isMultiCategory,
    isNfp,
    isTeamCaptain,
    managedAdditionalParticipantLabel,
    nonFundraisingParticipantLabel,
    participantCommitmentLabel,
    participantEmail,
    participantName,
    registration,
    registrations,
    registrationIndex,
    removeAriaLabel,
    removeParticipantLinkLabel,
    removeRegistration,
    renderCategoryPrice = () => {},
    setAppShowRegistrationRole,
    setAppShowRemainingForm,
    teamCaptainLabel,
    teamName = undefined,
    updateAddAdditionalParticipantDisabled,
    updateCurrentRegistration,
    updateCurrentRegistrationCategoryAndCurrentRegistration = () => {},
}) => {
    // This is the Registration you are currently working on not the registration
    // whose Participant's Summary we are displaying here
    const hasCurrentRegistrationBeenSubmitted =
        !!registrations[currentRegistration]?.Has_Been_Saved;

    // This is the registration for Participant Summary that currently being mapped through
    const shouldDisplayActionButtons = registration?.Has_Been_Saved || false;

    let teamInfo;
    let nfpInfo;
    let manageInfo;
    let commitmentInfo;
    const changeParticipantInfoLink = (
        <>
            {!isConfirmed && (
                <>
                    <Icon
                        className="-color--system-error"
                        glyph="exclamation-circle-solid"
                        type="FontAwesome"
                    />{" "}
                </>
            )}
            {isCms ? (
                <TextButton ariaLabel={changeParticipantDetailsLinkLabel}>
                    <strong>{changeParticipantDetailsLinkLabel}</strong>
                </TextButton>
            ) : (
                <ParticipantSummaryChangeButton
                    baseUrl={baseUrl}
                    changeParticipantDetailsLinkLabel={
                        changeParticipantDetailsLinkLabel
                    }
                    currentRegistration={currentRegistration}
                    isConfirmed={isConfirmed}
                    hasCurrentRegistrationBeenSubmitted={
                        hasCurrentRegistrationBeenSubmitted
                    }
                    participantName={participantName}
                    registration={registration}
                    registrationIndex={registrationIndex}
                    removeRegistration={removeRegistration}
                    setAppShowRemainingForm={setAppShowRemainingForm}
                    setAppShowRegistrationRole={setAppShowRegistrationRole}
                    updateCurrentRegistration={updateCurrentRegistration}
                    updateCurrentRegistrationCategoryAndCurrentRegistration={
                        updateCurrentRegistrationCategoryAndCurrentRegistration
                    }
                />
            )}
        </>
    );
    const removeParticipantLink = registrationIndex > 0 && (
        <>
            {" • "}
            {isCms ? (
                <TextButton ariaLabel={removeAriaLabel}>
                    <strong>{removeParticipantLinkLabel}</strong>
                </TextButton>
            ) : (
                <ParticipantSummaryRemoveButton
                    addAdditionalParticipantDisabled={
                        addAdditionalParticipantDisabled
                    }
                    currentRegistration={currentRegistration}
                    removeRegistration={removeRegistration}
                    registrations={registrations}
                    registrationIndex={registrationIndex}
                    updateAddAdditionalParticipantDisabled={
                        updateAddAdditionalParticipantDisabled
                    }
                    removeAriaLabel={removeAriaLabel}
                    removeParticipantLinkLabel={removeParticipantLinkLabel}
                />
            )}
        </>
    );
    if (teamName && `${teamName}`.length > 0) {
        teamInfo = (
            <>
                {teamName}
                {isTeamCaptain && (
                    <>
                        <span className="-margin-left--10">•</span>
                        <Icon
                            className="-margin-left--10 -margin-right--10"
                            glyph="crown-regular"
                            type="FontAwesome"
                        />
                        {teamCaptainLabel}
                    </>
                )}
                <br />
            </>
        );
    }
    if (isNfp) {
        nfpInfo = (
            <>
                <Icon
                    className="-margin-right--10"
                    glyph="exclamation-circle-solid"
                    type="FontAwesome"
                />
                {nonFundraisingParticipantLabel}
                <br />
            </>
        );
    }
    if (isManagedByPrimary) {
        manageInfo = (
            <>
                <Icon
                    className="-margin-right--10"
                    glyph="exclamation-circle-solid"
                    type="FontAwesome"
                />
                {managedAdditionalParticipantLabel}
                <br />
            </>
        );
    }
    if (isManagedByPrimary) {
        manageInfo = (
            <>
                <Icon
                    className="-margin-right--10"
                    glyph="exclamation-circle-solid"
                    type="FontAwesome"
                />
                {managedAdditionalParticipantLabel}
                <br />
            </>
        );
    }
    if (hasCommitment) {
        commitmentInfo = (
            <>
                <Icon
                    className="-margin-right--10"
                    glyph="bullseye-arrow-regular"
                    type="FontAwesome"
                />
                {participantCommitmentLabel}
                <br />
            </>
        );
    }
    return (
        <>
            {/* eslint-disable-next-line tailwindcss/classnames-order */}
            <div className="grid grid--no-gutters">
                <div className="col col--8">
                    <small>
                        {participantName}
                        <br />
                        {isMultiCategory && categoryName && (
                            <>
                                {categoryName}
                                <br />
                            </>
                        )}

                        {displayDetails && (
                            <>
                                {teamInfo}
                                {participantEmail}
                                {!isManagedByPrimary && <br />}
                                {commitmentInfo}
                                {manageInfo}
                                {nfpInfo}

                                {shouldDisplayActionButtons && (
                                    <>
                                        {changeParticipantInfoLink}
                                        {removeParticipantLink}
                                    </>
                                )}
                            </>
                        )}
                    </small>
                </div>
                <div className="col col--4 -text--right">
                    {categoryId && isCms && renderCategoryPrice(categoryId)}
                    {categoryId && !isCms && (
                        <ConnectedParticipantSummaryCategoryPrice
                            categoryId={categoryId}
                            registrationIndex={registrationIndex}
                        />
                    )}
                </div>
            </div>
            {displayDivider && (
                <Divider classNames="registration-summary__divider" dashed />
            )}
        </>
    );
};

ParticipantSummary.propTypes = {
    // Flag for when add participant is disabled from limits
    addAdditionalParticipantDisabled: PropTypes.bool.isRequired,
    // Start of navigate url which includes event alias
    baseUrl: PropTypes.string.isRequired,
    // Id of the category selected for a registration
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Name of the selected category
    categoryName: PropTypes.string,
    // Text to use inside "change" button
    changeParticipantDetailsLinkLabel: PropTypes.string.isRequired,
    // Number that represents current registration to be displayed on Registration Page
    currentRegistration: PropTypes.number.isRequired,
    // Flag telling us to display the remainder of the participant's details
    displayDetails: PropTypes.bool.isRequired,
    // Flag telling us to display the divider above the participant's details
    displayDivider: PropTypes.bool.isRequired,
    // Flag telling us to display the commitment info
    hasCommitment: PropTypes.bool,
    isCms: PropTypes.bool.isRequired,
    // Flag telling us that the registration has been confirmed by user
    isConfirmed: PropTypes.bool.isRequired,
    // Flag tell us this Fundraising Page will be managed by the Primary Registrant
    isManagedByPrimary: PropTypes.bool.isRequired,
    // Flag telling us multiple categories are available
    isMultiCategory: PropTypes.bool.isRequired,
    // Flag telling us this participant is a non fundraising participant
    isNfp: PropTypes.bool.isRequired,
    // Flag telling us that this participant is a team captain
    isTeamCaptain: PropTypes.bool.isRequired,
    // Text used to describe that primary participant is managing this participant
    managedAdditionalParticipantLabel: PropTypes.string.isRequired,
    // Text used to describe non fundraising participant
    nonFundraisingParticipantLabel: PropTypes.string.isRequired,
    // Participant's email
    participantEmail: PropTypes.string.isRequired,
    // Participant's name
    participantName: PropTypes.string.isRequired,
    // Participant commitment information
    participantCommitmentLabel: PropTypes.string.isRequired,
    // Registration data for registrant in redux
    registration: PropTypes.shape({ Has_Been_Saved: PropTypes.bool })
        .isRequired,
    registrations: PropTypes.arrayOf(
        PropTypes.shape({ Has_Been_Saved: PropTypes.bool }),
    ).isRequired,
    // Index of this participant's registration data in redux store
    registrationIndex: PropTypes.number.isRequired,
    // Aria label for remove link
    removeAriaLabel: PropTypes.string.isRequired,
    // Text to use inside "remove" button
    removeParticipantLinkLabel: PropTypes.string.isRequired,
    // Function to bind to the `remove` button
    removeRegistration: PropTypes.func.isRequired,
    // Redux action to handle display of registration role card
    setAppShowRegistrationRole: PropTypes.func.isRequired,
    // Redux action to handle display of form after registration role card
    setAppShowRemainingForm: PropTypes.func.isRequired,
    // Text used to describe a team captain
    teamCaptainLabel: PropTypes.string.isRequired,
    // Participant's team's name
    teamName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Redux action for updating addAdditionalParticipantDisabled state
    updateAddAdditionalParticipantDisabled: PropTypes.func.isRequired,
    // CMS display of Category Price using ThemeControls
    renderCategoryPrice: PropTypes.func,
    // Redux action to update currentRegistration
    updateCurrentRegistration: PropTypes.func.isRequired,
    // Redux action to update currentRegistration and currentRegistrationCategory
    updateCurrentRegistrationCategoryAndCurrentRegistration:
        PropTypes.func.isRequired,
};

export default ParticipantSummary;
